import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios'
import type { ValidationErrorBody } from './types'

export interface ApiClientOptions {
  /** e.g. "/api/v1" or "/papi/v1" — same origin as the SPA. */
  baseURL: string
  /** Endpoint that sets the XSRF-TOKEN cookie. */
  csrfUrl?: string
  /** Called on any 401, e.g. to clear cached auth and route to /login. */
  onUnauthorized?: () => void
}

export interface ApiClient extends AxiosInstance {
  /** Fetch the CSRF cookie; cached until `force` or a failure. */
  ensureCsrf: (force?: boolean) => Promise<void>
}

interface RetriableConfig extends InternalAxiosRequestConfig {
  _csrfRetried?: boolean
}

const SAFE_METHODS = new Set(['get', 'head', 'options'])

/**
 * Same-origin session client: cookies + CSRF, Sanctum style.
 *
 * A 419 means the session or token went stale (for example after a long idle
 * tab). The client refreshes the CSRF cookie and replays the request once.
 */
export function createApiClient({
  baseURL,
  csrfUrl = '/sanctum/csrf-cookie',
  onUnauthorized,
}: ApiClientOptions): ApiClient {
  const http = axios.create({
    baseURL,
    withCredentials: true,
    withXSRFToken: true,
    xsrfCookieName: 'XSRF-TOKEN',
    xsrfHeaderName: 'X-XSRF-TOKEN',
    headers: {
      Accept: 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
  })

  let csrf: Promise<void> | null = null

  const ensureCsrf = (force = false): Promise<void> => {
    if (force || csrf === null) {
      csrf = axios
        .get(csrfUrl, { withCredentials: true, headers: { Accept: 'application/json' } })
        .then(() => undefined)
        .catch((error: unknown) => {
          csrf = null
          throw error
        })
    }
    return csrf
  }

  http.interceptors.request.use(async (config) => {
    if (!SAFE_METHODS.has((config.method ?? 'get').toLowerCase())) {
      await ensureCsrf()
    }
    return config
  })

  http.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
      if (error instanceof AxiosError) {
        const config = error.config as RetriableConfig | undefined
        const status = error.response?.status

        if (status === 419 && config && !config._csrfRetried) {
          config._csrfRetried = true
          await ensureCsrf(true)
          return http.request(config)
        }

        if (status === 401) onUnauthorized?.()
      }
      return Promise.reject(error)
    },
  )

  return Object.assign(http, { ensureCsrf })
}

export function statusOf(error: unknown): number | null {
  return error instanceof AxiosError ? (error.response?.status ?? null) : null
}

/** A human message for any API failure. */
export function errorMessage(error: unknown, fallback = 'Something went wrong. Please try again.'): string {
  if (error instanceof AxiosError) {
    const body = error.response?.data as Partial<ValidationErrorBody> | undefined
    if (body && typeof body.message === 'string' && body.message !== '') return body.message
    if (error.response?.status === 429) return 'Too many attempts. Please wait a moment and try again.'
    if (error.response?.status === 403) return 'You do not have permission to do that.'
    if (!error.response) return 'Cannot reach the server. Check your connection.'
  }
  return fallback
}

/** Field errors from a 422, keyed by field name (first message each). */
export function fieldErrors(error: unknown): Record<string, string> {
  if (!(error instanceof AxiosError) || error.response?.status !== 422) return {}
  const body = error.response.data as Partial<ValidationErrorBody> | undefined
  const out: Record<string, string> = {}
  for (const [field, messages] of Object.entries(body?.errors ?? {})) {
    if (messages[0]) out[field] = messages[0]
  }
  return out
}

/**
 * Push a 422's field errors into a react-hook-form `setError`. Returns true
 * when at least one field matched, so callers can fall back to a toast.
 */
export function applyFieldErrors<F extends string>(
  error: unknown,
  setError: (name: F, error: { type: string; message: string }) => void,
  fields: readonly F[],
): boolean {
  const errors = fieldErrors(error)
  let matched = false
  for (const [key, message] of Object.entries(errors)) {
    const root = key.split('.')[0] ?? key
    const field = (fields as readonly string[]).includes(key)
      ? key
      : (fields as readonly string[]).includes(root)
        ? root
        : null
    if (field !== null) {
      setError(field as F, { type: 'server', message })
      matched = true
    }
  }
  return matched
}
